import { useState } from "react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

import { SectionCard } from "../../../../components/design-system/admin/SectionCard";

// interactive_floor (rugs) is retired — no longer offered as a vendor area.
export const VENDOR_AREAS = ["floor", "walls", "ceiling"] as const;

type ProfileField = "name" | "alias" | "email" | "storeUrl";

const labelClass = "mb-1.5 text-xs font-medium text-muted-foreground";

/**
 * Who the vendor is: name, alias, email, store, the surfaces they sell for, and
 * whether they can sign in. The plan and its limits live on the Plan tab.
 */
export const VendorGeneralInformation = ({ vendor, setVendor, submitted }) => {
    const { t } = useTranslation();
    // Alias is the visualizer-auth identifier and is referenced from
    // running visualizer sessions — it must not be edited after creation.
    const isEditMode = Boolean((vendor as any)?._id);
    const [touched, setTouched] = useState<Partial<Record<ProfileField, boolean>>>({});

    const handleChange = (event) => {
        const { name, value } = event.target;
        setTouched({ ...touched, [name]: true });
        setVendor({ ...vendor, [name]: value });
    };

    const handleToggleEnabled = (checked: boolean) => {
        setVendor({ ...vendor, isEnabled: checked });
    };

    const handleToggleArea = (area: string) => {
        if (vendor.areas?.find((va) => va.name.toLowerCase() === area.toLowerCase()))
            setVendor({
                ...vendor,
                areas: vendor.areas.filter((va) => va.name.toLowerCase() !== area.toLowerCase()),
            });
        else
            setVendor({
                ...vendor,
                areas: [...(vendor.areas ?? []), { name: area.toLowerCase() }],
            });
    };

    const isInvalid = (name: ProfileField) => (submitted && !vendor[name]) || (touched[name] && !vendor[name]);
    const areasMissing = submitted && (!vendor.areas || vendor.areas.length === 0);
    const enabled = vendor?.isEnabled !== false;

    return (
        <SectionCard
            title={t("ADMIN_VENDOREDIT.PROFILE.TITLE")}
            description={t("ADMIN_VENDOREDIT.PROFILE.DESCRIPTION")}
            testId="vendor-profile-card"
        >
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div>
                    <Label htmlFor="vendor-name" className={labelClass}>
                        {t("ADMIN_VENDOREDIT.PROFILE.NAME")} *
                    </Label>
                    <Input
                        id="vendor-name"
                        aria-invalid={isInvalid("name") || undefined}
                        name="name"
                        placeholder={t("ADMIN_VENDOREDIT.PROFILE.NAME_PLACEHOLDER")}
                        value={vendor?.name || ""}
                        onChange={handleChange}
                        required
                    />
                </div>

                <div>
                    <Label htmlFor="vendor-alias" className={labelClass}>
                        {t("ADMIN_VENDOREDIT.PROFILE.ALIAS")} *
                    </Label>
                    <Input
                        id="vendor-alias"
                        aria-invalid={isInvalid("alias") || undefined}
                        name="alias"
                        placeholder={t("ADMIN_VENDOREDIT.PROFILE.ALIAS_PLACEHOLDER")}
                        value={vendor?.alias || ""}
                        onChange={handleChange}
                        required
                        disabled={isEditMode}
                    />
                    {isEditMode && (
                        <p className="mt-1.5 text-xs text-muted-foreground">
                            {t("SUPER_ADMIN.EDIT_VENDOR.ALIAS_LOCKED_HINT")}
                        </p>
                    )}
                </div>

                <div className="md:col-span-2">
                    <Label htmlFor="vendor-email" className={labelClass}>
                        {t("ADMIN_VENDOREDIT.PROFILE.EMAIL")} *
                    </Label>
                    <Input
                        id="vendor-email"
                        aria-invalid={isInvalid("email") || undefined}
                        name="email"
                        placeholder={t("ADMIN_VENDOREDIT.PROFILE.EMAIL_PLACEHOLDER")}
                        value={vendor?.email || ""}
                        onChange={handleChange}
                        type="email"
                        required
                    />
                </div>

                <div className="md:col-span-2">
                    <Label htmlFor="vendor-store-url" className={labelClass}>
                        {t("ADMIN_VENDOREDIT.PROFILE.STORE_URL")} *
                    </Label>
                    <Input
                        id="vendor-store-url"
                        aria-invalid={isInvalid("storeUrl") || undefined}
                        name="storeUrl"
                        placeholder={t("ADMIN_VENDOREDIT.PROFILE.STORE_URL_PLACEHOLDER")}
                        value={vendor?.storeUrl || ""}
                        onChange={handleChange}
                        type="url"
                        required
                    />
                </div>

                <fieldset className="md:col-span-2">
                    <legend className="mb-2 text-xs font-medium text-muted-foreground">
                        {t("ADMIN_VENDOREDIT.PROFILE.AREAS")} *
                    </legend>
                    <div className="flex flex-wrap gap-2">
                        {VENDOR_AREAS.map((area) => {
                            const isSelected = !!vendor.areas?.find((e) => e.name.toLowerCase() === area.toLowerCase());
                            return (
                                <button
                                    key={area}
                                    type="button"
                                    aria-pressed={isSelected}
                                    onClick={() => handleToggleArea(area)}
                                    data-testid={`vendor-area-${area}`}
                                    className={cn(
                                        "cursor-pointer rounded-lg border bg-background px-3 py-1 text-[13px] transition-colors",
                                        // Selected = ink outline, never a tint.
                                        isSelected
                                            ? "border-foreground font-medium ring-1 ring-inset ring-foreground"
                                            : "text-muted-foreground hover:bg-accent hover:text-foreground",
                                    )}
                                >
                                    {t(`ADMIN_VENDOREDIT.PROFILE.AREA_${area.toUpperCase()}`)}
                                </button>
                            );
                        })}
                    </div>
                    {areasMissing && (
                        <p className="mt-2 text-xs text-destructive" role="alert">
                            {t("ADMIN_VENDOREDIT.PROFILE.AREAS_REQUIRED")}
                        </p>
                    )}
                </fieldset>

                <div className="md:col-span-2">
                    <Label className="flex w-full items-start gap-3 rounded-lg border p-4 font-normal">
                        <Switch checked={enabled} onCheckedChange={handleToggleEnabled} />
                        <span className="flex flex-col gap-0.5">
                            <span className="text-sm font-medium">{t("ADMIN_VENDOREDIT.PROFILE.STATUS_TITLE")}</span>
                            <span className="text-xs text-muted-foreground">
                                {enabled
                                    ? t("ADMIN_VENDOREDIT.PROFILE.STATUS_ENABLED_HINT")
                                    : t("ADMIN_VENDOREDIT.PROFILE.STATUS_DISABLED_HINT")}
                            </span>
                        </span>
                    </Label>
                </div>
            </div>
        </SectionCard>
    );
};
